import { useEffect, useState } from 'react'

import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Whether CENSO ACCESS (the register of the accesses, ADR-0030) is active for
 * the current tenant **and** open to the user's role.
 *
 * The module is segregated: only the area's roles, a role that names it and
 * the administrators open it, so a 403 on `/access/status` is the normal
 * answer for most users and reads as «off». Like CENSO MED, the area and its
 * menu appear only when this says `true`; until the answer arrives it is
 * `false`. The answer is kept per tenant.
 */
export interface StatoAccess {
  attivo: boolean
  pronto?: boolean
  /** The role writes in the area (Responsabile degli accessi), or only reads (Revisore). */
  scrive?: boolean
}

const SPENTO: StatoAccess = { attivo: false }
const memoria = new Map<string, Promise<StatoAccess>>()

function chiedi(tenant: string): Promise<StatoAccess> {
  let p = memoria.get(tenant)
  if (!p) {
    p = api
      .accessStatus()
      .then((r) => ({ attivo: Boolean(r.licensed), pronto: true, scrive: Boolean(r.write) }))
      .catch((e: { status?: number }) => {
        // A 403 is final for this role; any other error is asked again next time.
        if (e?.status !== 403) memoria.delete(tenant)
        return { attivo: false, pronto: true }
      })
    memoria.set(tenant, p)
  }
  return p
}

export function useStatoAccess(): StatoAccess {
  const { principal } = useAuth()
  const tenant = principal?.tenant_id ?? ''
  const [stato, setStato] = useState<StatoAccess>(SPENTO)
  useEffect(() => {
    if (!tenant) {
      setStato(SPENTO)
      return
    }
    let vivo = true
    chiedi(tenant).then((v) => {
      if (vivo) setStato(v)
    })
    return () => {
      vivo = false
    }
  }, [tenant])
  return stato
}
